/**
 * Copyright © 2026 Adnuntius AS.
 */
import {
  ChangeDetectionStrategy,
  Component,
  EffectRef,
  effect,
  Inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  signal,
  SimpleChanges
} from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';

import {deferSignalViewWrite, notifySignalView} from '../common/util/template-state';
import {StatsApiService} from '../common/controller/stats-api.service';
import {BurnRatesApiService} from './burn-rates-api.service';
import template from './line-item-analysis-page.component.html';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LineItemModel} from './line-item-api.service';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';

type LineItemAnalysisTab =
  | 'lineItem'
  | 'creatives'
  | 'tags'
  | 'reports'
  | 'articles'
  | 'co2'
  | 'traffic'
  | 'location'
  | 'domain'
  | 'reach'
  | 'diagnostics';

const objToStatRef: Record<string, {stat: string; isCost: boolean}> = {
  IMPRESSION: {stat: 'impressions', isCost: false},
  BUDGET: {stat: 'totalCost', isCost: true},
  CLICK: {stat: 'clicks', isCost: false},
  CONVERSION: {stat: 'conversions', isCost: false},
  VISIBLE_IMPRESSION: {stat: 'visibles', isCost: false},
  VIEWABLE_IMPRESSION: {stat: 'viewables', isCost: false},
  RENDERED_IMPRESSION: {stat: 'rendered', isCost: false}
};

@Component({
  standalone: false,
  selector: 'adn-line-item-analysis-page',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LineItemAnalysisPageComponent implements OnChanges, OnDestroy {
  @Input() state: SignalAdminResourcePageState<LineItemModel>;
  @Input() lineItemView: any;

  get model(): LineItemModel {
    return this.state?.model();
  }

  get readOnly(): boolean {
    return this.state?.readOnly() ?? true;
  }

  get isNew(): boolean {
    return this.state?.isNew() ?? false;
  }

  effectiveIsNew = false;
  activeTab: LineItemAnalysisTab = 'lineItem';
  showingChart: string | null = 'LINE_ITEM';
  showingReports = false;
  showingArticles = false;
  showingCo2 = false;
  showingTraffic = false;
  showingLocation = false;
  showingDomain = false;
  showingReach = false;
  hasCreativeTag = false;
  showControlInfo = false;

  canSeeReports = false;
  perms: any = {};
  isPureMarketplace = false;
  domainChartsEnabled = false;
  blockPubLink = false;
  showStats = true;
  chartCurrency: any;

  lineItem: any;
  apiModel: any;
  notesSaveTarget: any;
  creatives: any = {};
  creativesEmpty = true;
  creativeCount = 0;
  selCreative: any = null;

  delivery: any[] | null = null;
  objectiveDiagnostics: any = null;
  burnRateData: any = null;
  lastBurnRate: any = null;
  readonly viewRevision = signal(0);

  private stateEffect?: EffectRef;

  constructor(
    private readonly burnRatesApi: BurnRatesApiService,
    private readonly statsApi: StatsApiService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly injector: Injector
  ) {
    this.canSeeReports = LocalUserPermissions.getAnyVisibility().RUN_REPORTS === true
      || LocalUserPermissions.getAnyVisibility().AX_ADVERTISER === true;
    this.perms = LocalUserPermissions.getAllVisPermissions();
    this.isPureMarketplace = LocalNetworkProfile.isPureMarketplacePlatform();
    this.domainChartsEnabled = LocalNetworkProfile.isDomainCharts();
    this.blockPubLink = !(this.perms.MANAGE_PUBLISHING || this.perms.AX_PUBLISHER || this.perms.AX_PUBLISHER_ADMIN || this.perms.MANAGE_SYSTEM);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.state) {
      this.bindState();
    }
    if (changes.lineItemView) {
      this.applyModel();
      notifySignalView(this.viewRevision);
    }
  }

  ngOnDestroy(): void {
    this.stateEffect?.destroy();
  }

  selectTab(tab: LineItemAnalysisTab): void {
    this.activeTab = tab;
    if (tab === 'lineItem') {
      this.showingChart = 'LINE_ITEM';
    } else if (tab === 'creatives') {
      this.showingChart = 'CREATIVE';
    } else if (tab === 'tags') {
      this.hasCreativeTag = true;
    } else if (tab === 'reports') {
      this.showingReports = true;
    } else if (tab === 'articles') {
      this.showingArticles = true;
    } else if (tab === 'co2') {
      this.showingCo2 = true;
    } else if (tab === 'traffic') {
      this.showingTraffic = true;
    } else if (tab === 'location') {
      this.showingLocation = true;
    } else if (tab === 'domain') {
      this.showingDomain = true;
    } else if (tab === 'reach') {
      this.showingReach = true;
    } else if (tab === 'diagnostics') {
      this.getBurnRates();
      this.getWinRates();
    }
  }

  getWinRates(): void {
    if (this.delivery) {
      return;
    }

    const timezone = this.LocalNetworkProfile.get('timeZone');
    const now = moment.tz(timezone);
    const dayLength = +now - +now.clone().subtract(1, 'd');
    const model = this.lineItem;

    if (_.includes(['READY', 'RUNNING'], model.executionState)) {
      const statsStartDate = moment.tz(model.startDate, timezone);
      this.statsApi.get({
        lineItemId: model.id,
        startDate: statsStartDate,
        groupBy: 'DAILY',
        showFilteredBidReasons: true,
        currency: this.chartCurrency
      }).subscribe({next: (response: any) => {
        const calculateDeliveryRates = (label: string, statsChunk: any) => {
          const data: any = Object.assign({}, statsChunk);
          const bids = statsChunk.bids || 0;
          const frequencyCapped = statsChunk.frequencyCapped || 0;
          const rateLimited = statsChunk.rateLimited || 0;
          const belowFloor = statsChunk.belowFloor || 0;
          const auctioned = bids - frequencyCapped - rateLimited - belowFloor;
          data.label = label;
          data.totalMatches = bids;
          data.auctionEntries = auctioned;
          data.impressions = statsChunk.impressions;
          data.rendered = statsChunk.rendered;
          data.cappedRate = bids <= 0 ? 0.0 : 1.0 * frequencyCapped / bids;
          data.smoothingRate = bids <= 0 ? 0.0 : 1.0 * rateLimited / bids;
          data.belowFloorRate = bids <= 0 ? 0.0 : 1.0 * belowFloor / bids;
          data.winRate = auctioned <= 0 ? 0.0 : 1.0 * statsChunk.impressions / auctioned;
          data.renderRate = statsChunk.impressions <= 0 ? 0.0 : 1.0 * statsChunk.rendered / statsChunk.impressions;
          return data;
        };

        const today = now.format('YYYY-MM-DD');
        const yesterday = now.clone().subtract(1, 'd').format('YYYY-MM-DD');
        const delivery: any[] = [];
        let deliveryYesterday: any = {};
        for (let i = 1; i < 3; i++) {
          if (response.chunks.length >= i) {
            const statsChunk = response.chunks[response.chunks.length - i];
            const chunkStart = statsChunk.chunkStart;
            if (chunkStart.startsWith(today)) {
              delivery.push(calculateDeliveryRates('Today', statsChunk));
            } else if (chunkStart.startsWith(yesterday)) {
              deliveryYesterday = calculateDeliveryRates('Yesterday', statsChunk);
              delivery.push(deliveryYesterday);
            }
          }
        }

        const objectiveDiagnostics: any[] = [];
        if (_.isDate(model.endDate) || model.endDate) {
          const remainingLength = +moment(model.endDate) - +now;
          const remainingDays = remainingLength / dayLength;
          _.forEach(this.lineItem.objectives, (num: any, oKey: string) => {
            const statRef = objToStatRef[oKey];
            if (!statRef) {
              return;
            }
            const diag: any = {type: oKey};
            diag.alreadyDelivered = _.get(response.totals, statRef.stat);
            if (!diag.alreadyDelivered && statRef.stat === 'totalCost') {
              diag.alreadyDelivered = _.get(response.totals, 'cost');
            }
            let objective = num;
            if (statRef.isCost) {
              diag.alreadyDelivered = _.get(diag.alreadyDelivered, 'amount') || 0;
              objective = _.get(objective, 'amount') || 0;
            }
            diag.deliveredYesterday = _.get(deliveryYesterday, statRef.stat) || 0;
            if (!diag.deliveredYesterday && statRef.stat === 'totalCost') {
              diag.deliveredYesterday = _.get(deliveryYesterday, 'cost');
            }
            const impressionsYesterday = _.get(deliveryYesterday, 'impressions') || 0;
            const totalMatchesYesterday = _.get(deliveryYesterday, 'totalMatches') || 0;
            if (statRef.isCost) {
              diag.deliveredYesterday = _.get(diag.deliveredYesterday, 'amount') || 0;
            }
            diag.possibleEventsYesterday = impressionsYesterday <= 0 ? 0 : (diag.deliveredYesterday / impressionsYesterday) * totalMatchesYesterday;
            diag.remaining = objective - diag.alreadyDelivered;
            diag.requiredPerDay = remainingDays <= 0 ? diag.remaining : diag.remaining / remainingDays;
            if (statRef.isCost) {
              const currency = _.get(num, 'currency') || 'USD';
              diag.alreadyDelivered = {amount: diag.alreadyDelivered, currency};
              diag.remaining = {amount: diag.remaining, currency};
              diag.requiredPerDay = {amount: diag.requiredPerDay, currency};
              diag.deliveredYesterday = {amount: diag.deliveredYesterday, currency};
              diag.possibleEventsYesterday = {amount: diag.possibleEventsYesterday, currency};
            } else {
              diag.alreadyDelivered = Math.round(diag.alreadyDelivered);
              diag.remaining = Math.round(diag.remaining);
              diag.requiredPerDay = Math.round(diag.requiredPerDay);
              diag.deliveredYesterday = Math.round(diag.deliveredYesterday);
              diag.possibleEventsYesterday = Math.round(diag.possibleEventsYesterday);
            }
            objectiveDiagnostics.push(diag);
          });
        }
        // Defer spinner [resolved]="objectiveDiagnostics" past checkNoChanges.
        deferSignalViewWrite(this.viewRevision, () => {
          this.delivery = delivery;
          this.objectiveDiagnostics = objectiveDiagnostics;
        });
      }, error: _.noop});
    } else {
      deferSignalViewWrite(this.viewRevision, () => {
        this.delivery = [];
        this.objectiveDiagnostics = [];
      });
    }
  }

  getBurnRates(): void {
    if (this.burnRateData) {
      return;
    }
    const now = moment();
    const weekAgo = now.clone().subtract(7, 'd');
    let startDate = this.lineItem.startDate || weekAgo;
    if (weekAgo > startDate) {
      startDate = weekAgo;
    }

    this.burnRatesApi.getForLineItem(this.lineItem.id, {startDate}).subscribe({
      next: (response: any) => {
        // Defer spinner [resolved]="burnRateData" past checkNoChanges.
        deferSignalViewWrite(this.viewRevision, () => {
          this.lastBurnRate = response.currentBurnRate;
          this.burnRateData = response.rows;
        });
      },
      error: _.noop
    });
  }

  private applyModel(): void {
    const view = this.lineItemView || {creatives: {}};
    this.creatives = this.state?.relatedData()?.creatives || view.creatives || {};
    this.creativesEmpty = _.isEmpty(this.creatives);
    this.creativeCount = Object.keys(this.creatives).length;
    this.selCreative = _.find(this.creatives, () => true) || null;
    this.lineItem = this.model;
    this.apiModel = this.model;
    this.notesSaveTarget = this.state?.notesSaveTarget();
    this.chartCurrency = _.get(this.model, ['bidSpecification', 'cpm', 'currency'])
      || _.get(this.model, ['bidSpecification', 'cpc', 'currency'])
      || _.get(this.model, ['objectives', 'BUDGET', 'currency']);
    this.showStats = this.model?.permission !== 'AX_PUBLISHER';
    this.effectiveIsNew = !!this.isNew;
  }

  private bindState(): void {
    this.stateEffect?.destroy();
    if (!this.state) {
      return;
    }
    this.applyModel();
    notifySignalView(this.viewRevision);
    this.stateEffect = effect(() => {
      this.state.model();
      this.state.relatedData();
      this.state.isNew();
      this.applyModel();
      notifySignalView(this.viewRevision);
    }, {injector: this.injector});
  }

}
